import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    AlertCircle,
    ArrowLeft,
    Building2,
    CheckCircle2,
    Copy,
    CreditCard,
    ExternalLink,
    Gift,
    Loader2,
    LogOut,
    Receipt,
    RefreshCw,
    Save,
    Link2,
    Search,
    Send,
    Store,
    Truck,
} from 'lucide-react';

import {
    getInitSettingAccounts,
    getInitSettingDetail,
    issueCardLink,
    requestCardRegistration,
    saveInitSettingRequest,
} from "../../api/wizard/initSetting.api";
import {logout} from "../../api/account/account.api";
/*
  비치물 어휘는 고객 온보딩 화면과 **같은 출처**(`libs/stickerCatalog.js`)를 쓴다(2026-10-12).
  두 화면이 목록을 따로 들면 색이 하나 추가될 때 한쪽만 늘어나고,
  담당자 화면에서 고를 수 없는 디자인을 고객이 고르는 상태가 된다.
  `stickerTypes` 는 옛 POP 값(`pop_type`)의 이름 표기에만 쓴다.
*/
import {stickerTypes} from "../form/StickerGeneratorForm";
import {
    STICKER_COLORS, STICKER_ORIENTATIONS, STICKER_FULFILLMENT,
    stickerTypeId, parseStickerType, stickerImageUrl, describeStickerType,
} from "../../libs/stickerCatalog";

/**
 * 초기 세팅 위저드 (`/sysadmin/wizard/init-setting`) — **설치 진행자용** 화면.
 *
 * ## 무엇을 하는 화면인가
 *
 * 고객과 통화하며 초기 세팅을 진행한 사람이, 그 통화에서 확인한 값을 남기고
 * **백엔드 연동 작업자에게 비동기로 넘긴다.** 종전에는 그 값이 사람의 메모에만 있었고,
 * 연동이 끝난 뒤 누군가 그 메모를 보고 AI 이벤트 생성을 손으로 실행해야 다음 단계가 시작됐다.
 *
 * ⚠️ `/sysadmin/wizard/backend`(`AdminChannelWizard.js`)와 **다른 화면**이다. 그쪽은
 * 백엔드 연동 작업자가 카카오 발신프로필·디벨로퍼 앱을 연결하는 곳이다. 담당자가 다르다.
 *
 * 설계: `_ai-runs/2026-09-07_002_install_wizard/001_analysis_and_design.md`
 *
 * ## 🔴 화면을 고칠 때 반드시 지킬 것
 *
 * 1. **'비즈니스 인증' 을 조회한 값처럼 표기하지 말 것.** 카카오 API 로는 채널의 비즈니스 인증 여부를
 *    알 수 없다(채널 프로필 응답이 `id`·`name`·`searchId` 3필드뿐이다). 이 화면의 인증 표시는
 *    **설치 진행자가 눈으로 보고 체크한 값**이다. 조사:
 *    `_ai-runs/2026-09-07_001_wizard_admin_channel_list_api_and_biz_verification/001_analysis.md`
 *    반면 **신용카드 등록 여부는 실제 조회값**이다(`billing_info`).
 *
 * 2. **입력 순서(DOM 순서)를 바꾸지 말 것.** '이벤트 회원가입 세팅'(1~5등)과 '자동화 메시지 세팅'
 *    (재방문·생일)은 화면에서 좌우 2컬럼으로 보이지만 **DOM 은 좌측 5개 → 우측 2개 순서**다.
 *    그래야 Tab 이 `1등 → 2등 → … → 5등 → 재방문 → 생일` 로 흐른다.
 *    🔴 CSS grid 로 셀을 번갈아 배치하면 Tab 이 `1등 → 재방문 → 2등 → …` 으로 튄다.
 *    `tabIndex` 로 교정하는 방법은 쓰지 않았다 — 양수 `tabIndex` 는 페이지 전체의 탭 순서를
 *    끌어올려 상단 버튼·헤더보다 먼저 걸리게 만든다.
 */

/** 초기 세팅 진행 상태 배지 색. 서버 enum(`ChannelInstallRequest.Status`)과 이름이 같다. */
const STATUS_STYLE = {
    DRAFT: 'bg-gray-100 text-gray-600',
    /** 설치비 미결제(2026-10-12). 결제가 확인되기 전에는 연동을 진행하지 않는다 — 눈에 띄는 색. */
    PAYMENT_PENDING: 'bg-rose-100 text-rose-700',
    BIZ_PENDING: 'bg-amber-100 text-amber-700',
    BACKEND_READY: 'bg-blue-100 text-blue-700',
    BACKEND_DONE: 'bg-emerald-100 text-emerald-700',
    AUTO_SETUP_DONE: 'bg-emerald-100 text-emerald-700',
    CONFIRM_SENT: 'bg-violet-100 text-violet-700',
    CONFIRM_REVISED: 'bg-orange-100 text-orange-700',
    CONFIRMED: 'bg-emerald-600 text-white',
    CANCELED: 'bg-gray-200 text-gray-500',
};

/**
 * 서버 `CommonResponse` 의 성공 코드.
 *
 * 🔴 **200 이 아니다.** `ResultCode.OK` 의 값은 `2000` 이다
 * (`redirect-service` `config/response/ResultCode.kt`). 200 과 비교하면 **성공 응답이 전부
 * 실패로 판정**되고, 그때 `res.data`(객체)를 문자열로 찍어 `[object Object]` 가 화면에 뜬다.
 * 2026-09-08 로컬 확인에서 실제로 그렇게 나왔다.
 */
const RESULT_OK = 2000;

/**
 * 옛 POP 값(`pop_type`, 단종)의 선택지. 옛 신청의 값을 정정·삭제할 때만 쓴다.
 * 🔴 **가격은 여기 적지 않는다** — 서버 `InstallPricing` 이 소유한다(가격이 바뀔 때 고쳐야 할 곳이 둘이면 한쪽이 낡는다).
 */
const POP_OPTIONS = stickerTypes.filter((t) => t.id.startsWith('ver_'));

/**
 * 수령 방식 선택지. 값은 서버 `ChannelInstallRequest.StickerFulfillment` 와 같다.
 * `''` = 미정(옛 행). 🔑 "이미지만" 이 **명시 상태**인 것이 사용자가 지적한 빈자리의 답이다.
 */
const FULFILLMENT_OPTIONS = [
    {id: STICKER_FULFILLMENT.IMAGE_ONLY, name: '이미지만 받음 (무료 · 발송 없음)'},
    {id: STICKER_FULFILLMENT.PRINT, name: '제작 · 택배 발송'},
];

/** 빈 폼. 신청이 없는 계정을 처음 열 때 쓴다. */
const EMPTY_FORM = {
    displayName: '',
    benefitRank1: '',
    benefitRank2: '',
    benefitRank3: '',
    benefitRank4: '',
    benefitRank5: '',
    retargetBenefit: '',
    birthdayBenefit: '',
    /*
      스티커 = 색상 × 방향 → 저장 시 `sticker_type`(`hor_blue` 등)으로 합친다(2026-10-12).
      색상이 비면 '선택 없음'. 방향 기본값은 가로형.
    */
    stickerColor: '',
    stickerOrientation: STICKER_ORIENTATIONS[0].id,
    stickerFulfillment: '',
    stickerQuantity: '',
    /* POP — 단종. 옛 신청에 값이 있을 때만 화면에 보인다. */
    popType: '',
    popQuantity: '',
    deliveryReceiver: '',
    deliveryPhoneNumber: '',
    deliveryZipCode: '',
    deliveryAddress: '',
    deliveryAddressDetail: '',
    bizVerified: false,
    benefitsEntered: false,
    memo: '',
};

/**
 * 서버가 준 실패 사유를 사람이 읽을 문자열로.
 *
 * `CommonResponse.data` 는 문자열일 수도, 객체일 수도 있다. 객체를 그대로 렌더하면
 * `[object Object]` 가 되어 담당자가 아무 것도 알 수 없다.
 */
const messageOf = (value, fallback) => {
    if (typeof value === 'string' && value.trim()) return value;
    if (value && typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (e) {
            return fallback;
        }
    }
    return fallback;
};

/**
 * 다음(카카오) 우편번호 서비스 스크립트. **키가 필요 없고 무료**다.
 *
 * 🔑 페이지 로드 때가 아니라 **'주소 검색' 을 누른 순간에만** 내려받는다.
 * 이 화면은 계정 목록이 주 용도라 대부분의 진입에서 주소를 건드리지 않는다.
 */
const DAUM_POSTCODE_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

/** 스크립트를 한 번만 붙이고 `daum.Postcode` 생성자를 준다. 실패하면 reject. */
const loadDaumPostcode = () => new Promise((resolve, reject) => {
    if (window.daum?.Postcode) {
        resolve(window.daum.Postcode);
        return;
    }

    const existing = document.querySelector(`script[src="${DAUM_POSTCODE_SRC}"]`);
    if (existing) {
        // 이미 붙어 있으면(연타) 새로 만들지 않고 그 로딩에 편승한다.
        existing.addEventListener('load', () => (
            window.daum?.Postcode ? resolve(window.daum.Postcode) : reject(new Error('daum.Postcode 없음'))
        ));
        existing.addEventListener('error', () => reject(new Error('스크립트 로드 실패')));
        return;
    }

    const script = document.createElement('script');
    script.src = DAUM_POSTCODE_SRC;
    script.async = true;
    script.onload = () => (
        window.daum?.Postcode ? resolve(window.daum.Postcode) : reject(new Error('daum.Postcode 없음'))
    );
    script.onerror = () => reject(new Error('스크립트 로드 실패'));
    document.head.appendChild(script);
});

/** 수량 입력값(문자열) → 서버에 보낼 정수. 빈칸·0·음수·문자는 전부 null(주문 없음). */
const toQuantity = (raw) => {
    if (raw === '' || raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

export default function InitSettingWizard() {
    const [step, setStep] = useState(1);

    const [accounts, setAccounts] = useState([]);
    const [isAccountsLoading, setIsAccountsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedAccount, setSelectedAccount] = useState(null);

    const [detail, setDetail] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    /** 카드 등록 여부를 **언제** 조회한 값인지. 담당자가 고객에게 "지금 확인해보니" 라고 말할 근거다. */
    const [cardCheckedAt, setCardCheckedAt] = useState(null);
    const [isCardRefreshing, setIsCardRefreshing] = useState(false);

    const [form, setForm] = useState(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);
    const [isCardSending, setIsCardSending] = useState(false);
    /**
     * 주소를 손으로 칠 수 있게 열어 둔 상태.
     *
     * 평소에는 `false` — 우편번호·기본주소는 **검색으로만** 채운다(오타 방지).
     * 스크립트를 못 불러왔을 때만 `true` 가 되어 직접 입력을 허용한다. 외부 스크립트 하나 때문에
     * 담당자가 아무 것도 저장하지 못하는 상태를 만들지 않기 위한 탈출구다.
     */
    const [isAddressManual, setIsAddressManual] = useState(false);
    /** 발급된 카드 등록 고유링크. `{url, expiresAt}` 또는 실패 메시지. */
    const [cardLink, setCardLink] = useState(null);
    const [isLinkIssuing, setIsLinkIssuing] = useState(false);
    /** 마지막 동작 결과. `{type: 'ok'|'error', text}`. 알럿 대신 화면에 남겨 담당자가 읽게 한다. */
    const [notice, setNotice] = useState(null);

    const loadAccounts = useCallback(() => {
        setIsAccountsLoading(true);
        getInitSettingAccounts()
            .then(({data}) => setAccounts(data ?? []))
            .catch(() => setNotice({type: 'error', text: '계정 목록을 불러오지 못했습니다.'}))
            .finally(() => setIsAccountsLoading(false));
    }, []);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    /**
     * 검색은 이름·ID·전화번호·사업자번호를 함께 본다.
     *
     * 설치 진행자가 방금 통화한 상대를 찾는 화면이라 손에 있는 단서가 그때그때 다르다
     * (가게 이름을 계정명으로 쓴 경우, 전화번호만 아는 경우 모두 있다).
     */
    const filteredAccounts = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return accounts;

        const digits = q.replace(/\D/g, '');
        return accounts.filter((acc) => {
            // 매장명도 검색 대상이다 — 목록에 보이는 값으로 찾을 수 있어야 한다.
            const haystack = [acc.installDisplayName, acc.name, acc.id, acc.phoneNumber]
                .filter(Boolean).join(' ').toLowerCase();
            if (haystack.includes(q)) return true;
            if (!digits) return false;
            const bizNumbers = (acc.bizRegNumbers ?? []).map((n) => String(n).replace(/\D/g, ''));
            const phone = String(acc.phoneNumber ?? '').replace(/\D/g, '');
            return phone.includes(digits) || bizNumbers.some((n) => n.includes(digits));
        });
    }, [accounts, searchTerm]);

    /**
     * 두번째 화면 진입 — 서버가 준 값으로 폼을 채운다.
     *
     * ⚠️ 서버는 미입력을 `null` 로 준다. `null` 을 그대로 input value 에 넣으면 React 가
     * uncontrolled 로 취급해 경고를 내고 입력이 어긋난다 ⇒ 전부 빈 문자열로 정규화한다.
     *
     * 🔴 **매장명(`displayName`)에 계정명을 넣지 않는다**(2026-09-08). 계정명은 보통 **사람 이름**이라
     * 초기값으로 채우면 담당자가 그것을 매장명으로 착각해 그대로 저장한다. 비워 두고 커서를 놓아
     * 화면을 열자마자 매장명부터 치게 한다.
     */
    const openAccount = (account) => {
        setSelectedAccount(account);
        setNotice(null);
        setCardLink(null);
        setIsDetailLoading(true);
        setStep(2);

        getInitSettingDetail(account.id)
            .then(({data}) => {
                setDetail(data);
                setCardCheckedAt(new Date());
                const req = data?.request;
                const savedSticker = parseStickerType(req?.stickerType ?? data?.suggestedStickerType);
                setForm(req ? {
                    displayName: req.displayName ?? '',
                    benefitRank1: req.benefitRank1 ?? '',
                    benefitRank2: req.benefitRank2 ?? '',
                    benefitRank3: req.benefitRank3 ?? '',
                    benefitRank4: req.benefitRank4 ?? '',
                    benefitRank5: req.benefitRank5 ?? '',
                    retargetBenefit: req.retargetBenefit ?? '',
                    birthdayBenefit: req.birthdayBenefit ?? '',
                    stickerColor: savedSticker?.color ?? '',
                    stickerOrientation: savedSticker?.orientation ?? STICKER_ORIENTATIONS[0].id,
                    stickerFulfillment: req.stickerFulfillment ?? '',
                    stickerQuantity: req.stickerQuantity != null ? String(req.stickerQuantity) : '',
                    popType: req.popType ?? '',
                    popQuantity: req.popQuantity != null ? String(req.popQuantity) : '',
                    deliveryReceiver: req.deliveryReceiver ?? '',
                    deliveryPhoneNumber: req.deliveryPhoneNumber ?? '',
                    deliveryZipCode: req.deliveryZipCode ?? '',
                    deliveryAddress: req.deliveryAddress ?? '',
                    deliveryAddressDetail: req.deliveryAddressDetail ?? '',
                    bizVerified: !!req.bizVerified,
                    benefitsEntered: !!req.benefitsEntered,
                    memo: req.memo ?? '',
                } : {
                    ...EMPTY_FORM,
                    /*
                      신청이 아직 없으면 **고객이 옛 온보딩에서 고른 값**으로 시작한다.
                      서버가 `account_channel.sticker_type` 한 칸을 스티커·POP 두 칸으로 나눠 준다.

                      🟡 2026-09-10 부터 고객이 `/wizard` 를 지나면 **신청이 반드시 생기므로**
                         이 분기는 사실상 **옛 고객·담당자 직접 입력** 전용이다.

                      🔴 신청이 이미 있으면 이 제안값을 쓰지 않는다(위 분기). 담당자가 고친 값도,
                         고객이 신청서에 적은 값도 온보딩 값으로 되돌리면 안 된다.
                    */
                    stickerColor: savedSticker?.color ?? '',
                    stickerOrientation: savedSticker?.orientation ?? STICKER_ORIENTATIONS[0].id,
                    popType: data?.suggestedPopType ?? '',
                    // 수령인·연락처는 계정 정보로 시작한다. 비치물은 보통 가입자가 받는다.
                    deliveryReceiver: account.name ?? '',
                    deliveryPhoneNumber: account.phoneNumber ?? '',
                });
            })
            .catch(() => setNotice({type: 'error', text: '초기 세팅 정보를 불러오지 못했습니다.'}))
            .finally(() => setIsDetailLoading(false));
    };

    /**
     * 카드 등록 여부만 다시 조회한다.
     *
     * ⚠️ **`form` 은 건드리지 않는다.** 담당자가 입력 중에 새로고침을 누르는 상황이라
     * 여기서 폼을 덮어쓰면 치던 내용이 사라진다. 서버 조회값(`detail`)만 갈아 끼운다.
     */
    const refreshCard = () => {
        if (!selectedAccount) return;
        setIsCardRefreshing(true);
        getInitSettingDetail(selectedAccount.id)
            .then(({data}) => {
                setDetail(data);
                setCardCheckedAt(new Date());
            })
            .catch(() => setNotice({type: 'error', text: '카드 등록 상태를 다시 확인하지 못했습니다.'}))
            .finally(() => setIsCardRefreshing(false));
    };

    const backToList = () => {
        setStep(1);
        setDetail(null);
        setSelectedAccount(null);
        setNotice(null);
        // 목록의 상태 배지를 갱신한다 — 방금 접수한 계정이 '백엔드 연동 요청' 으로 보여야 한다.
        loadAccounts();
    };

    const setField = (key) => (e) => setForm((prev) => ({...prev, [key]: e.target.value}));

    /** 저장할 `sticker_type`. 색상이 없으면 '' (= 서버 NULL, 선택 없음). */
    const stickerType = stickerTypeId(form.stickerOrientation, form.stickerColor);
    const isImageOnly = form.stickerFulfillment === STICKER_FULFILLMENT.IMAGE_ONLY;

    /**
     * 보낼 물건이 있는가 = 수령 정보가 필요한가. 서버 `Dto.needsDeliveryInfo()` 와 **같은 판정**이다.
     *
     * 🔑 (2026-10-12) 디자인이 있고 수령 방식이 **'이미지만' 이 아니면** 발송한다. 수령 방식이 비어 있는
     *    옛 행은 종전처럼 "디자인 있으면 발송" 이다. 옛 POP 주문이 있어도 발송한다.
     */
    const needsDelivery = (!!form.stickerColor && !isImageOnly) || !!form.popType;

    /** 초기 세팅비 결제 여부 — ✅ 실제 조회값(`install_payment`). */
    const setupPaid = detail?.setupPayment?.paid === true;

    /**
     * 저장. `status` 가 null 이면 기존 상태를 유지한다(단순 저장).
     *
     * ⚠️ 서버가 허용하는 status 는 `DRAFT`·`BIZ_PENDING`·`BACKEND_READY`·`CANCELED` 뿐이다.
     * 그 외 값을 보내면 거절된다(뒷단 자동화 상태를 화면이 임의로 세우지 못하게).
     *
     * 🔴 **설치비 미결제인데 '바로 연동 요청'** 은 사용자 규칙("결제하지 않은 고객은 세팅을 진행하지 않는다")에
     *    어긋나므로 confirm 으로 한 번 막는다. 막기만 하고 금지하지는 않는다 — 계좌이체 등 예외가 있다.
     */
    const submit = (status, successText) => {
        if (!selectedAccount) return;

        if (status === 'BACKEND_READY' && !setupPaid) {
            if (!window.confirm(
                '초기 세팅비가 아직 결제되지 않았습니다.\n' +
                '설치비를 결제하지 않은 고객은 세팅을 진행하지 않는 것이 원칙입니다.\n' +
                '그래도 백엔드 연동을 요청할까요? (계좌이체 등 별도 결제를 확인한 경우에만)'
            )) return;
        }

        setIsSaving(true);
        setNotice(null);

        const {stickerColor, stickerOrientation, ...rest} = form;
        saveInitSettingRequest({
            accountId: selectedAccount.id,
            ...rest,
            stickerType,
            stickerFulfillment: stickerType ? (form.stickerFulfillment || null) : null,
            // '이미지만' 은 발송할 물건이 없다 — 수량을 보내지 않는다(서버도 같은 규칙으로 NULL 로 눕힌다).
            stickerQuantity: isImageOnly ? null : toQuantity(form.stickerQuantity),
            popQuantity: toQuantity(form.popQuantity),
            status,
        })
            .then((res) => {
                // 응답이 통째로 없는 경우 = fetch 계층이 오류를 삼킨 것(checkAuthorization).
                // 여기서 막지 않으면 아래 `res.data` 에서 TypeError 가 나 화면이 조용히 멈춘다.
                if (!res) {
                    setNotice({type: 'error', text: '저장 결과를 확인할 수 없습니다. 새로고침 후 다시 시도해주세요.'});
                    return;
                }
                // 🔴 성공 코드는 2000 이다(200 아님). ResultCode.OK 참고 — 위 RESULT_OK 주석.
                if (res.code !== RESULT_OK) {
                    setNotice({type: 'error', text: messageOf(res.data, '저장에 실패했습니다.')});
                    return;
                }
                setDetail((prev) => (prev ? {...prev, request: res.data} : prev));
                setNotice({type: 'ok', text: successText});
            })
            .catch(() => setNotice({type: 'error', text: '저장에 실패했습니다.'}))
            .finally(() => setIsSaving(false));
    };

    /**
     * 신용카드 등록 요청 알림톡 발송.
     *
     * 🔴 **실제 고객에게 메시지가 나간다.** 그래서 confirm 을 한 번 받는다.
     * 🔴 템플릿(`request_card_registration`)이 아직 SureM 에 등록돼 있지 않아 지금은 거절될 수 있다 —
     * 그때 벤더 사유가 화면에 그대로 보이도록 서버가 결과를 내려준다. 조용히 성공한 것처럼 보이지 않게.
     */
    const sendCardRequest = () => {
        if (!selectedAccount) return;
        const phone = detail?.accountPhoneNumber ?? '(연락처 없음)';
        if (!window.confirm(`${selectedAccount.name} (${phone}) 님에게 카드 등록 요청 알림톡을 실제로 발송합니다.\n계속할까요?`)) {
            return;
        }

        setIsCardSending(true);
        setNotice(null);

        requestCardRegistration(selectedAccount.id)
            .then((res) => {
                const result = res?.data;
                if (result?.sent) {
                    setNotice({type: 'ok', text: '카드 등록 요청 알림톡을 발송했습니다.'});
                    refreshCard();  // 발송 시각이 panel 에 반영되도록
                } else {
                    setNotice({
                        type: 'error',
                        text: `발송하지 못했습니다. (코드 ${result?.code ?? '알 수 없음'}) ${result?.message ?? ''}`,
                    });
                }
            })
            .catch(() => setNotice({type: 'error', text: '발송 요청 중 오류가 발생했습니다.'}))
            .finally(() => setIsCardSending(false));
    };

    /**
     * 다음 우편번호 검색 팝업.
     *
     * `roadAddress`(도로명)를 우선 쓰고, 없으면 `jibunAddress`(지번)로 떨어진다 —
     * 신축·일부 지역은 도로명이 비어 온다.
     */
    const openAddressSearch = () => {
        loadDaumPostcode()
            .then((Postcode) => {
                new Postcode({
                    oncomplete: (data) => {
                        setForm((prev) => ({
                            ...prev,
                            deliveryZipCode: data.zonecode ?? '',
                            deliveryAddress: data.roadAddress || data.jibunAddress || '',
                        }));
                        setNotice(null);
                    },
                }).open();
            })
            .catch(() => {
                setIsAddressManual(true);
                setNotice({
                    type: 'error',
                    text: '주소 검색을 불러오지 못했습니다. 주소를 직접 입력해주세요(우편번호 포함).',
                });
            });
    };

    /**
     * 카드 등록 고유링크 발급.
     *
     * 🔴 사업자등록번호가 없으면 서버가 거절한다 — 고객이 본인 확인을 할 값이 없으면
     * 링크는 "토큰만 알면 통과" 가 되어 구 링크와 같은 상태가 된다. 그 안내를 그대로 보여준다.
     * 재발급하면 **앞서 보낸 링크는 잠긴다**(서버).
     */
    const createCardLink = () => {
        if (!selectedAccount) return;

        setIsLinkIssuing(true);
        setCardLink(null);
        setNotice(null);

        issueCardLink(selectedAccount.id)
            .then((res) => {
                const data = res?.data;
                if (!data?.issued) {
                    setNotice({type: 'error', text: data?.message ?? '링크를 만들지 못했습니다.'});
                    return;
                }
                setCardLink({url: data.url, expiresAt: data.expiresAt});
                setNotice({type: 'ok', text: '고유링크를 만들었습니다. 이전에 보낸 링크는 더 이상 동작하지 않습니다.'});
            })
            .catch(() => setNotice({type: 'error', text: '링크 발급 중 오류가 발생했습니다.'}))
            .finally(() => setIsLinkIssuing(false));
    };

    /**
     * 링크 복사. `navigator.clipboard` 는 https 또는 localhost 에서만 동작하므로
     * 실패 시 입력창을 선택해 주는 것으로 대신한다(담당자가 Ctrl+C 로 복사할 수 있다).
     */
    const copyCardLink = (url) => {
        const done = () => setNotice({type: 'ok', text: '링크를 복사했습니다.'});
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).then(done).catch(() =>
                setNotice({type: 'error', text: '복사하지 못했습니다. 링크를 직접 선택해 복사해주세요.'})
            );
            return;
        }
        setNotice({type: 'error', text: '이 환경에서는 자동 복사가 안 됩니다. 링크를 직접 선택해 복사해주세요.'});
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    const request = detail?.request;

    return (
        <div className="bg-gray-50 flex flex-col items-center justify-center p-4 py-10">
            <style>{`
        @keyframes slideUpFade {
          0% { opacity: 0; transform: translateY(15px); filter: blur(3px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
        }
        .animate-slide-up { animation: slideUpFade 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            <div className="relative w-full min-h-screen bg-white rounded-3xl shadow-sm p-6 sm:p-10 animate-slide-up">

                <button
                    onClick={handleLogout}
                    className="absolute top-6 right-6 sm:top-8 sm:right-8 flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-gray-600 font-medium transition-colors z-50 bg-white/80 px-2 py-1 rounded-md backdrop-blur-sm"
                >
                    <LogOut className="w-3.5 h-3.5"/>
                    로그아웃
                </button>

                {/* ================= STEP 1: 계정 선택 ================= */}
                {step === 1 && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">초기 세팅을 진행한 계정을 선택해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">
                            가장 최근에 회원가입한 계정이 위에 옵니다. 방금 통화하며 온보딩을 안내한 계정을 고르세요.
                        </p>

                        <div className="relative mb-4">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                <Search className="w-5 h-5 text-gray-400"/>
                            </span>
                            <input
                                type="text"
                                placeholder="매장명 · 이름 · ID · 전화번호 · 사업자번호로 검색"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-400"
                            />
                        </div>

                        {isAccountsLoading ? (
                            <div className="flex items-center justify-center py-20 text-gray-400 gap-2 text-[14px]">
                                <Loader2 className="w-5 h-5 animate-spin"/>
                                계정 목록을 불러오는 중입니다.
                            </div>
                        ) : (
                            <div className="space-y-3 mb-8 max-h-[calc(100vh_-_320px)] overflow-y-auto no-scrollbar pr-2">
                                {filteredAccounts.length > 0 ? filteredAccounts.map((acc) => (
                                    <div
                                        key={acc.id}
                                        onClick={() => openAccount(acc)}
                                        className="p-5 rounded-2xl border bg-white border-gray-200 hover:border-blue-300 transition-all flex items-center justify-between cursor-pointer"
                                    >
                                        <div>
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                {/* 이미 접수한 계정을 다시 골라 값을 덮어쓰는 사고를 막는 배지 */}
                                                {acc.installStatus && (
                                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${STATUS_STYLE[acc.installStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                                        {acc.installStatusLabel ?? acc.installStatus}
                                                    </span>
                                                )}
                                                {/*
                                                  🔑 **매장명을 계정명보다 앞·크게** 둔다(2026-09-08 사용자 요청).
                                                     담당자가 다시 찾아 들어갈 때 계정명(사람 이름)보다 업체명으로 찾는 것이 수월하다.
                                                     아직 저장 전이면 매장명이 없으니 그때는 계정명이 제목이 된다.
                                                */}
                                                {acc.installDisplayName ? (
                                                    <>
                                                        <h4 className="font-bold text-gray-900">{acc.installDisplayName}</h4>
                                                        <span className="text-[12px] text-gray-400">{acc.name}</span>
                                                    </>
                                                ) : (
                                                    <h4 className="font-bold text-gray-900">{acc.name}</h4>
                                                )}
                                            </div>
                                            <div className="text-[13px] text-gray-500 flex items-center gap-3 flex-wrap">
                                                <span>ID: <strong className="text-gray-700">{acc.id}</strong></span>
                                                <span className="w-1 h-1 rounded-full bg-gray-300"/>
                                                <span>전화번호: <strong className="text-gray-700">{acc.phoneNumber ?? '-'}</strong></span>
                                                <span className="w-1 h-1 rounded-full bg-gray-300"/>
                                                <span>가입: <strong className="text-gray-700">{acc.createdAt}</strong></span>
                                            </div>
                                            {(acc.bizRegNumbers ?? []).length > 0 && (
                                                <div className="text-[13px] text-gray-500 mt-1">
                                                    사업자번호: <strong className="text-gray-700">{acc.bizRegNumbers.join(', ')}</strong>
                                                </div>
                                            )}
                                        </div>
                                        <ExternalLink className="w-5 h-5 text-gray-300 shrink-0"/>
                                    </div>
                                )) : (
                                    <div className="text-center py-16 text-gray-400 text-[14px]">
                                        검색 결과와 일치하는 계정이 없습니다.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ================= STEP 2: 설치 정보 입력 ================= */}
                {step === 2 && (
                    <div className="flex flex-col h-full">
                        <button
                            onClick={backToList}
                            className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-800 font-medium mb-4 w-fit"
                        >
                            <ArrowLeft className="w-4 h-4"/>
                            계정 목록으로
                        </button>

                        {isDetailLoading ? (
                            <div className="flex items-center justify-center py-20 text-gray-400 gap-2 text-[14px]">
                                <Loader2 className="w-5 h-5 animate-spin"/>
                                초기 세팅 정보를 불러오는 중입니다.
                            </div>
                        ) : (
                            <>
                                {/*
                                  ===== 진행 — 🔑 화면 맨 위 =====

                                  담당자가 화면을 열자마자 하는 일이 **매장명 입력**이다(통화 직후라
                                  "참숯불갈비 논현점" 이 머리에 있는 상태). 그래서 이 박스가 가장 위에 오고,
                                  매장명 입력창에 커서가 미리 놓인다.

                                  계정명(사람 이름)을 초기값으로 넣지 않는다 — 그대로 저장돼 담당자간
                                  소통용 이름이 사람 이름이 되어 버린다.

                                  🔴 **액션 버튼은 여기 두지 않는다.** 맨 아래에 있다(2026-09-08 사용자 요청) —
                                     모든 입력을 마친 뒤 누르는 것이 흐름에 맞다.
                                */}
                                <Panel title="진행" icon={<Store className="w-4 h-4 text-blue-600"/>} accent
                                       badge={request?.status && (
                                           <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${STATUS_STYLE[request.status] ?? 'bg-gray-100 text-gray-600'}`}>
                                               {request.statusLabel ?? request.status}
                                           </span>
                                       )}>
                                    <p className="text-[12px] text-gray-500 mb-4">
                                        선택한 계정: <strong className="text-gray-700">{selectedAccount?.name}</strong>
                                        <span className="text-gray-400"> ({selectedAccount?.id})</span>
                                    </p>

                                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                                        매장명 / 업체명 <span className="font-normal text-gray-400">— 담당자간 소통용. 정식 카카오 채널명이 아니어도 됩니다</span>
                                    </label>
                                    <input
                                        type="text"
                                        autoFocus
                                        value={form.displayName}
                                        onChange={setField('displayName')}
                                        placeholder="예: 참숯불갈비 논현점"
                                        maxLength={50}
                                        className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-xl text-[16px] font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-300"
                                    />

                                    <div className="mt-3">
                                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">메모</label>
                                        <textarea
                                            value={form.memo}
                                            onChange={setField('memo')}
                                            rows={2}
                                            placeholder="통화에서 나온 특이사항 (선택)"
                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-400 resize-none"
                                        />
                                    </div>
                                </Panel>

                                {/* ===== 기본 상태 정보 panel ===== */}
                                <div className="mb-8 rounded-2xl border border-gray-200 bg-gray-50/70 p-5">
                                    <h3 className="text-[13px] font-bold text-gray-500 mb-4 tracking-wide">기본 상태 정보</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {/* 카카오 채널 비즈니스 인증 — 🔴 조회값이 아니다 */}
                                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Building2 className="w-4 h-4 text-gray-400"/>
                                                <span className="text-[13px] font-semibold text-gray-700">카카오 채널 비즈니스 인증</span>
                                            </div>

                                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                                <input
                                                    type="checkbox"
                                                    checked={form.bizVerified}
                                                    onChange={(e) => setForm((prev) => ({...prev, bizVerified: e.target.checked}))}
                                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className={`text-[14px] font-bold ${form.bizVerified ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                    {form.bizVerified ? '인증 확인함' : '인증 미확인'}
                                                </span>
                                            </label>

                                            {/*
                                              🔴 이 문구를 빼지 말 것. 카카오 API 로는 채널의 비즈니스 인증 여부를 알 수 없다
                                                 (채널 프로필 응답이 id·name·searchId 3필드뿐이다). 이 체크는 사람이 적는 메모다.
                                                 조회한 값처럼 보이면 담당자가 확인 없이 다음 단계로 넘긴다.
                                            */}
                                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                                시스템이 조회한 값이 아닙니다. 카카오는 채널의 인증 여부를 API 로 제공하지 않으므로
                                                직접 확인한 뒤 체크해주세요.
                                            </p>

                                            {detail?.bizVerifyUrl ? (
                                                <button
                                                    onClick={() => window.open(detail.bizVerifyUrl, '_blank', 'noopener,noreferrer')}
                                                    className="mt-3 flex items-center justify-center gap-1.5 w-full text-blue-600 bg-blue-50 hover:bg-blue-100 py-2 rounded-lg text-[13px] font-medium transition-colors"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5"/>
                                                    카카오 비즈니스에서 확인
                                                </button>
                                            ) : (
                                                <p className="mt-3 text-[11px] text-gray-400">
                                                    채널 공개 ID 가 아직 없어 확인 링크를 만들 수 없습니다(연동 전).
                                                </p>
                                            )}

                                            {request?.bizVerifiedAt && (
                                                <p className="mt-2 text-[11px] text-gray-400">확인 표시: {request.bizVerifiedAt}</p>
                                            )}
                                        </div>

                                        {/* 신용카드 등록 — ✅ 실제 조회값. 조회 시각 + 새로고침 + 요청 발송 */}
                                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <CreditCard className="w-4 h-4 text-gray-400"/>
                                                    <span className="text-[13px] font-semibold text-gray-700">신용카드 등록</span>
                                                </div>
                                                <button
                                                    onClick={refreshCard}
                                                    disabled={isCardRefreshing}
                                                    title="다시 조회"
                                                    className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 disabled:text-gray-300 transition-colors"
                                                >
                                                    <RefreshCw className={`w-3.5 h-3.5 ${isCardRefreshing ? 'animate-spin' : ''}`}/>
                                                    새로고침
                                                </button>
                                            </div>

                                            <div className="flex items-center gap-2 mb-1">
                                                {detail?.hasCard
                                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500"/>
                                                    : <AlertCircle className="w-4 h-4 text-amber-500"/>}
                                                <span className={`text-[14px] font-bold ${detail?.hasCard ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                    {detail?.hasCard ? `등록됨 (${detail.cardCount}장)` : '미등록'}
                                                </span>
                                            </div>

                                            {/*
                                              조회 시각을 남기는 이유 = 담당자가 고객에게 카드 등록을 요청하고 기다리는 화면이다.
                                              "언제 본 값인지" 가 없으면 방금 등록했는데 화면이 예전 값인 것을 알 수 없다.
                                            */}
                                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                                결제 카드 정보에서 조회한 실제 값입니다.
                                                {cardCheckedAt && (
                                                    <> 조회 시각 <strong className="text-gray-500">{cardCheckedAt.toLocaleTimeString('ko-KR')}</strong></>
                                                )}
                                            </p>

                                            <button
                                                onClick={sendCardRequest}
                                                disabled={isCardSending}
                                                /*
                                                  🔴 색을 넣은 버튼이다(2026-09-08). 종전에는 흰 배경 + 회색 테두리라
                                                     담당자가 **버튼인 줄 몰랐다**.

                                                  🔑 **카드가 이미 등록돼 있으면 강조하지 않는다**(2026-09-08 사용자 요청).
                                                     그때 이 버튼은 "굳이 안 해도 되는 일" 이라 눈에 띌 이유가 없다.
                                                     기능은 그대로 둔다 — 카드를 바꾸려는 경우가 있다.
                                                */
                                                className={`mt-3 flex items-center justify-center gap-1.5 w-full py-2.5 rounded-lg text-[13px] font-semibold transition-colors disabled:bg-gray-200 disabled:text-gray-400 ${
                                                    detail?.hasCard
                                                        ? 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                                                        : 'text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm'
                                                }`}
                                            >
                                                {isCardSending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
                                                신용카드 등록 요청 메시지 발송
                                            </button>

                                            {/*
                                              고유링크 — 고객이 로그인 없이 **사업자등록번호만 맞히면** 카드를 등록한다.
                                              🔴 사업자번호가 없으면 만들 수 없다(고객이 확인할 값이 없으면 링크는
                                                 '토큰만 알면 통과' 가 되어 구 링크와 같은 상태가 된다).
                                            */}
                                            <button
                                                onClick={createCardLink}
                                                disabled={isLinkIssuing || !detail?.canIssueCardLink}
                                                className={`mt-2 flex items-center justify-center gap-1.5 w-full py-2.5 rounded-lg text-[13px] font-semibold transition-colors disabled:bg-gray-100 disabled:text-gray-400 ${
                                                    detail?.hasCard
                                                        ? 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                                                        : 'text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100'
                                                }`}
                                            >
                                                {isLinkIssuing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Link2 className="w-3.5 h-3.5"/>}
                                                카드 등록 고유링크 만들기
                                            </button>

                                            {!detail?.canIssueCardLink && (
                                                <p className="mt-2 text-[11px] text-amber-600 leading-relaxed">
                                                    이 채널에 <strong>사업자등록번호가 없어</strong> 고유링크를 만들 수 없습니다.
                                                    고객이 본인 확인을 할 값이 없기 때문입니다. 사업자등록번호를 먼저 등록해주세요.
                                                </p>
                                            )}

                                            {cardLink && (
                                                <div className="mt-2 rounded-lg bg-white border border-gray-200 p-3">
                                                    <p className="text-[11px] text-gray-400 mb-1">
                                                        고객에게 보낼 링크 · {cardLink.expiresAt} 까지
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={cardLink.url}
                                                            onFocus={(e) => e.target.select()}
                                                            className="flex-1 min-w-0 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-[11px] text-gray-700"
                                                        />
                                                        <button
                                                            onClick={() => copyCardLink(cardLink.url)}
                                                            className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 px-2.5 py-1.5 rounded transition-colors"
                                                        >
                                                            <Copy className="w-3 h-3"/>
                                                            복사
                                                        </button>
                                                    </div>
                                                    <p className="mt-2 text-[11px] text-gray-400 leading-relaxed">
                                                        이 링크로는 <strong className="text-gray-500">카드 등록만</strong> 가능합니다(조회·삭제 불가).
                                                        사업자등록번호를 5회 틀리면 잠깁니다.
                                                    </p>
                                                </div>
                                            )}

                                            {request?.cardRequestedAt && (
                                                <p className="mt-2 text-[11px] text-gray-400">
                                                    마지막 요청 발송: {request.cardRequestedAt} ({request.cardRequestCount}회)
                                                </p>
                                            )}
                                        </div>

                                        {/*
                                          초기 세팅비 결제 — ✅ 실제 조회값(`install_payment`, 2026-10-12).
                                          사용자 규칙: 설치비를 결제하지 않은 고객은 세팅을 진행하지 않는다. 그래서 카드 등록 옆에 나란히 둔다 —
                                          담당자가 "카드는 있는데 결제는 안 됐다" 를 한눈에 가른다.
                                          🔴 여기에 '결제 실행' 버튼을 두지 않았다(실카드 승인). 고객이 서비스 목록 > '설치비 결제하기' 로 직접 결제한다.
                                        */}
                                        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="setup-payment">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Receipt className="w-4 h-4 text-gray-400"/>
                                                <span className="text-[13px] font-semibold text-gray-700">초기 세팅비 결제</span>
                                            </div>

                                            <div className="flex items-center gap-2 mb-1">
                                                {setupPaid
                                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500"/>
                                                    : <AlertCircle className="w-4 h-4 text-rose-500"/>}
                                                <span className={`text-[14px] font-bold ${setupPaid ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                    {setupPaid
                                                        ? `결제 완료${detail?.setupPayment?.amount != null ? ` (${Number(detail.setupPayment.amount).toLocaleString()}원)` : ''}`
                                                        : '미결제'}
                                                </span>
                                            </div>

                                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                                {setupPaid
                                                    ? <>결제 일시 <strong className="text-gray-500">{detail?.setupPayment?.paidAt ?? '-'}</strong></>
                                                    : '설치비 + 스티커 비용(VAT 포함). 결제가 확인되면 신청이 연동 요청으로 자동 전환됩니다.'}
                                            </p>

                                            {!setupPaid && detail?.setupPayment?.lastFailMessage && (
                                                <p className="mt-2 text-[11px] text-rose-600 leading-relaxed">
                                                    마지막 시도 실패 ({detail.setupPayment.lastAttemptAt ?? '-'}): {detail.setupPayment.lastFailMessage}
                                                </p>
                                            )}

                                            {!setupPaid && (
                                                <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                                                    고객이 <strong>서비스 목록 › 설치비 결제하기</strong> 로 결제합니다.
                                                    카드가 없으면 먼저 위의 고유링크로 카드를 등록하게 하세요.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* 채널 진행 상태 — 연동 전에는 비어 있는 것이 정상이다 */}
                                    <div className="mt-4 text-[12px] text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
                                        <span>채널: <strong className="text-gray-700">{detail?.channelName ?? '미연결'}</strong>
                                            {detail?.channelId ? ` (#${detail.channelId})` : ''}</span>
                                        <span>검색 ID: <strong className="text-gray-700">{detail?.channelIdName ?? '-'}</strong></span>
                                        <span>사업자번호: <strong className="text-gray-700">{detail?.bizRegNumber ?? '-'}</strong></span>
                                        <span>발신프로필: <strong className="text-gray-700">{detail?.hasProfileKey ? '등록됨' : '미등록'}</strong></span>
                                        <span>연동 완료: <strong className="text-gray-700">{detail?.isSettingsCompleted ? '완료' : '미완료'}</strong></span>
                                    </div>
                                </div>

                                {/*
                                  ===== 혜택 설정 (이벤트 회원가입 + 자동화 메시지) =====

                                  🔴 **DOM 순서 = 좌측 5개 → 우측 2개.** 이 순서가 곧 Tab 순서다
                                     (1등 → 2등 → 3등 → 4등 → 5등 → 재방문 → 생일).
                                     컬럼을 두 개의 독립된 `<div>` 로 두었기 때문에 그렇게 된다.
                                     grid 셀을 번갈아 놓는 방식으로 바꾸면 Tab 이 좌우로 튄다.
                                     이 박스로 감싸도 안쪽 순서는 그대로다 — 감싸기만 하고 순서를 건드리지 않았다.
                                */}
                                <Panel title="혜택 설정" icon={<Gift className="w-4 h-4 text-blue-600"/>}>
                                    {/*
                                      🔑 두 영역을 **각각 흰 박스**로 묶는다(2026-09-08 사용자 요청).
                                         스티커/POP 박스 안쪽과 같은 모양이라 화면 전체가 일관된다.
                                      ⚠️ **DOM 순서는 그대로다** — 좌측 5개 → 우측 2개. 박스로 감싸기만 했다.
                                         Tab 이 1등 → … → 5등 → 재방문 → 생일 로 흐르는 것은 이 순서 덕이다.
                                    */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                                            <div className="text-[13px] font-bold text-gray-800 mb-0.5">이벤트 회원가입 세팅</div>
                                            <div className="text-[11px] text-gray-400 mb-3">고객이 제공 가능하다고 확인해 준 혜택. 등수 순서대로 입력하세요.</div>
                                            <div className="space-y-3">
                                                {[1, 2, 3, 4, 5].map((rank) => (
                                                    <Input
                                                        key={rank}
                                                        label={`${rank}등 혜택명`}
                                                        value={form[`benefitRank${rank}`]}
                                                        onChange={setField(`benefitRank${rank}`)}
                                                        placeholder={rank === 1 ? '예: 삼겹살 1인분 무료' : ''}
                                                        maxLength={100}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                                            <div className="text-[13px] font-bold text-gray-800 mb-0.5">자동화 메시지 세팅</div>
                                            <div className="text-[11px] text-gray-400 mb-3">기본 자동화 메시지 중 쿠폰이 실려 나가는 두 건입니다.</div>
                                            <div className="space-y-3">
                                                <Input label="재방문 혜택명" value={form.retargetBenefit}
                                                       onChange={setField('retargetBenefit')} maxLength={100}/>
                                                <Input label="생일 혜택명" value={form.birthdayBenefit}
                                                       onChange={setField('birthdayBenefit')} maxLength={100}/>
                                            </div>
                                        </div>
                                    </div>

                                    {/*
                                      혜택 입력 완료 체크 (2026-09-17, RevenueOS 연동 `benefits` 게이트의 근거).
                                      🔑 혜택명 칸이 채워졌는지로 판정하지 않는다(사용자 결정) — 몇 칸이 차야 "완료" 인지는
                                         매장마다 다르고, 확인 중인 값이 임시로 들어 있을 수 있다. 비즈니스 인증 체크와 같은
                                         **사람이 적는 플래그**다. 저장 시 `benefitsEntered` 로 함께 간다(form 스프레드).
                                      ⚠️ 위 grid **뒤에** 둔다 — 1등→…→생일 Tab 순서를 건드리지 않는다(taborder 테스트).
                                    */}
                                    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                data-testid="benefits-entered"
                                                checked={form.benefitsEntered}
                                                onChange={(e) => setForm((prev) => ({...prev, benefitsEntered: e.target.checked}))}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className={`text-[14px] font-bold ${form.benefitsEntered ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                {form.benefitsEntered ? '혜택 입력 완료' : '혜택 입력 미완료'}
                                            </span>
                                        </label>
                                        <p className="mt-1.5 text-[11px] text-gray-400 leading-relaxed">
                                            고객과 확인한 혜택 입력이 끝났으면 체크하세요. 이 체크가 RevenueOS(자동 안내) 의
                                            '혜택 입력' 완료 기준입니다 — 칸이 채워진 것만으로는 완료로 보지 않습니다.
                                        </p>
                                        {request?.benefitsEnteredAt && (
                                            <p className="mt-2 text-[11px] text-gray-400">완료 표시: {request.benefitsEnteredAt}</p>
                                        )}
                                    </div>
                                </Panel>

                                {/*
                                  ===== 스티커 / POP 발송 =====

                                  🔑 **스티커와 POP 은 별개 상품이고 둘 다 주문할 수 있다.**
                                     🟢 2026-09-10 부터 **고객 신청 화면도 두 칸으로 받는다**
                                        (`ChannelConnectWizard` STEP 6). 그 전에는 라디오 하나로 골라
                                        `account_channel.sticker_type` 한 칸에 넣어 '둘 다' 를 표현하지 못했고,
                                        그 시절 고객의 값만 아래 '옛 온보딩 선택값' 으로 보인다.

                                  🔑 **주문 여부 = 디자인이 골라졌는가**(수량이 아니다).
                                */}
                                <Panel title="스티커 발송" icon={<Truck className="w-4 h-4 text-blue-600"/>}>
                                    <p className="text-[12px] text-gray-500 mb-1">
                                        고객이 신청서에서 고른 값이 채워집니다. 필요하면 수정하세요.
                                        <strong className="text-gray-700"> '이미지만 받음' 이면 발송할 물건이 없습니다.</strong>
                                    </p>
                                    {/*
                                      🟡 **옛 고객 전용 표기**(2026-09-10). 그 이전에 가입해 `account_channel.sticker_type`
                                         **한 칸**에만 값이 있는 고객을 위해 남긴다 — 값이 있을 때만 보인다.
                                    */}
                                    {detail?.onboardingStickerType && (
                                        <p className="text-[11px] text-gray-400 mb-4">
                                            옛 온보딩 선택값: <strong className="text-gray-600">
                                                {stickerTypes.find((t) => t.id === detail.onboardingStickerType)?.name
                                                    ?? (detail.onboardingStickerType === 'none' ? '제작하지 않음' : '없음')}
                                            </strong>
                                            <span className="text-gray-300"> · 당시 화면은 한 가지만 고를 수 있었습니다</span>
                                        </p>
                                    )}

                                    {/*
                                      스티커 = 색상 × 방향 × 수령 방식 × 수량 (2026-10-12). POP 은 선택지에서 뺐다(단종).
                                      🔴 가격 문구를 여기 적지 않는다 — 서버 `InstallPricing` 이 소유하고 고객 화면이 그것을 받아 그린다.
                                    */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                                            <div className="text-[13px] font-bold text-gray-800 mb-0.5">스티커 디자인</div>
                                            <div className="text-[11px] text-gray-400 mb-3">
                                                방수형 · 가로형 150 × 100mm / 세로형 100 × 150mm
                                            </div>
                                            <div className="space-y-3">
                                                <Select label="색상" value={form.stickerColor}
                                                        onChange={setField('stickerColor')}
                                                        options={STICKER_COLORS.map((c) => ({id: c.id, name: c.label}))}
                                                        noneLabel="선택 없음"/>
                                                <Select label="방향" value={form.stickerOrientation}
                                                        onChange={setField('stickerOrientation')}
                                                        options={STICKER_ORIENTATIONS.map((o) => ({id: o.id, name: `${o.label} (${o.spec})`}))}
                                                        noneLabel={null}/>
                                            </div>
                                            {form.stickerColor && (
                                                <div className="mt-3 flex items-center gap-3">
                                                    <img
                                                        src={stickerImageUrl(form.stickerOrientation, form.stickerColor)}
                                                        alt={describeStickerType(stickerType)}
                                                        className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-gray-50"
                                                    />
                                                    <span className="text-[12px] text-gray-500">{describeStickerType(stickerType)} · <code className="text-gray-400">{stickerType}</code></span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                                            <div className="text-[13px] font-bold text-gray-800 mb-0.5">수령 방식 · 수량</div>
                                            <div className="text-[11px] text-gray-400 mb-3">
                                                고객 신청 화면의 '이미지만 받을게요 / 20장 / 추가 수량' 에 대응합니다.
                                            </div>
                                            <div className="space-y-3">
                                                <Select label="수령 방식" value={form.stickerFulfillment}
                                                        onChange={setField('stickerFulfillment')}
                                                        options={FULFILLMENT_OPTIONS} noneLabel="미정"
                                                        disabled={!form.stickerColor}/>
                                                <Input label="수량 (장)" value={form.stickerQuantity} type="number"
                                                       onChange={setField('stickerQuantity')}
                                                       disabled={!form.stickerColor || isImageOnly}
                                                       placeholder={!form.stickerColor
                                                           ? '색상을 고르면 입력할 수 있습니다'
                                                           : (isImageOnly ? '이미지만 — 수량 없음' : '기본 20 · 10장 단위')}/>
                                            </div>
                                            {isImageOnly && (
                                                <p className="mt-3 text-[11px] text-emerald-700 leading-relaxed" data-testid="image-only-note">
                                                    이미지만 받는 고객입니다. 제작·발송 없이 QR·스티커 이미지만 전달하면 됩니다.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/*
                                      옛 POP 주문(단종) — 값이 있는 옛 신청에서만 보인다. 정정·삭제용으로 칸을 남긴다.
                                      새 신청에는 이 블록이 나타나지 않는다.
                                    */}
                                    {(request?.popType || form.popType) && (
                                        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 mb-4" data-testid="legacy-pop">
                                            <div className="text-[13px] font-bold text-gray-700 mb-0.5">POP (단종 · 옛 신청값)</div>
                                            <div className="text-[11px] text-gray-400 mb-3">
                                                254 × 355mm 폼보드. 새 신청에서는 받지 않습니다. 비우면 주문 없음으로 저장됩니다.
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <Select label="디자인" value={form.popType}
                                                        onChange={setField('popType')}
                                                        options={POP_OPTIONS} noneLabel="주문 안 함"/>
                                                <Input label="수량 (개)" value={form.popQuantity} type="number"
                                                       onChange={setField('popQuantity')}
                                                       disabled={!form.popType}
                                                       placeholder={form.popType ? '예: 1' : ''}/>
                                            </div>
                                        </div>
                                    )}

                                    {/*
                                      ===== 수령 정보 — 스티커·POP 공용, 하나의 박스 =====

                                      🔑 **우편번호는 입력창이 아니다**(2026-09-08 사용자 요청). '주소 검색' 으로 기본주소가
                                         들어오면 **자동으로 표시만** 된다. 사람이 칠 수 있게 두면 기본주소와 어긋난 우편번호가
                                         남을 수 있고, 그 조합은 택배 접수에서 걸러지지 않는다.
                                      🟡 스크립트를 못 불러왔을 때만(`isAddressManual`) 기본주소·우편번호를 직접 입력하게 연다 —
                                         외부 스크립트 하나 때문에 담당자가 아무 것도 저장하지 못하는 상태를 만들지 않는다.
                                    */}
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <div className="text-[13px] font-bold text-gray-800 mb-0.5">수령 정보</div>
                                        <div className="text-[11px] text-gray-400 mb-3">
                                            {needsDelivery
                                                ? '스티커를 받는 매장 주소입니다.'
                                                : (isImageOnly
                                                    ? "'이미지만 받음' 이라 발송할 물건이 없습니다."
                                                    : '스티커 디자인을 고르면 입력할 수 있습니다.')}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                            <Input label="수령인" value={form.deliveryReceiver}
                                                   onChange={setField('deliveryReceiver')} maxLength={50}
                                                   disabled={!needsDelivery}/>
                                            <Input label="연락처" value={form.deliveryPhoneNumber}
                                                   onChange={setField('deliveryPhoneNumber')} maxLength={30}
                                                   disabled={!needsDelivery}/>
                                        </div>

                                        <div className="mb-3">
                                            <label className={`block text-[13px] font-semibold mb-1.5 ${needsDelivery ? 'text-gray-700' : 'text-gray-300'}`}>
                                                주소
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={openAddressSearch}
                                                    disabled={!needsDelivery}
                                                    className="shrink-0 flex items-center justify-center gap-1.5 bg-gray-900 text-white disabled:bg-gray-200 disabled:text-gray-400 hover:bg-black px-5 py-3 rounded-xl text-[13px] font-semibold transition-colors"
                                                >
                                                    <Search className="w-3.5 h-3.5"/>
                                                    주소 검색
                                                </button>
                                                {/*
                                                  우편번호 = **표시 전용**. input 이 아니라 텍스트다(사용자 요청).
                                                  값이 없을 때 자리를 잡아 두려고 최소 폭을 준다.
                                                */}
                                                {isAddressManual ? (
                                                    <input
                                                        type="text"
                                                        value={form.deliveryZipCode}
                                                        onChange={setField('deliveryZipCode')}
                                                        disabled={!needsDelivery}
                                                        maxLength={10}
                                                        placeholder="우편번호"
                                                        className="shrink-0 w-[110px] px-3 py-3 bg-white border border-amber-300 rounded-xl text-[13px] focus:outline-none"
                                                    />
                                                ) : (
                                                    <span className={`shrink-0 min-w-[86px] text-center px-3 py-3 rounded-xl border text-[13px] font-mono ${
                                                        form.deliveryZipCode
                                                            ? 'bg-gray-50 border-gray-200 text-gray-800'
                                                            : 'bg-gray-50 border-dashed border-gray-200 text-gray-300'
                                                    }`}>
                                                        {form.deliveryZipCode || '우편번호'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className={`block text-[13px] font-semibold mb-1.5 ${needsDelivery ? 'text-gray-700' : 'text-gray-300'}`}>기본 주소</label>
                                            <input
                                                type="text"
                                                value={form.deliveryAddress}
                                                readOnly={!isAddressManual}
                                                onChange={setField('deliveryAddress')}
                                                disabled={!needsDelivery}
                                                maxLength={300}
                                                placeholder={needsDelivery ? "'주소 검색' 으로 채워집니다" : ''}
                                                className={`w-full px-4 py-3 border rounded-xl text-[14px] focus:outline-none transition-all placeholder-gray-400 ${
                                                    !needsDelivery ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-50 border-gray-200'
                                                }`}
                                            />
                                        </div>

                                        <Input label="상세주소" value={form.deliveryAddressDetail}
                                               onChange={setField('deliveryAddressDetail')} maxLength={200}
                                               disabled={!needsDelivery}
                                               placeholder={needsDelivery ? '동 · 호수 등' : ''}/>

                                        {isAddressManual && (
                                            <p className="mt-2 text-[11px] text-amber-600">
                                                주소 검색을 불러오지 못해 직접 입력으로 전환했습니다. 우편번호도 함께 적어주세요.
                                            </p>
                                        )}
                                    </div>

                                    {!needsDelivery && (
                                        <p className="mt-2 text-[11px] text-gray-400">
                                            {isImageOnly
                                                ? '이미지만 받는 고객이라 발송할 것이 없습니다. 수령 정보는 비워 둡니다.'
                                                : '스티커를 주문하지 않아 발송할 것이 없습니다. 수령 정보는 비워 둡니다.'}
                                        </p>
                                    )}

                                </Panel>

                                {/*
                                  ===== 액션 — 🔑 화면 맨 아래 (2026-09-08 사용자 요청) =====
                                  모든 입력을 마친 뒤 누르는 것이 흐름에 맞다. 결과 배너도 버튼 바로 위에 둔다.
                                */}
                                {notice && (
                                    <div className={`mb-4 rounded-xl p-4 flex items-start gap-2 border ${
                                        notice.type === 'ok' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                                        {notice.type === 'ok'
                                            ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5"/>
                                            : <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5"/>}
                                        <p className={`text-[13px] leading-relaxed ${
                                            notice.type === 'ok' ? 'text-emerald-800' : 'text-red-800'}`}>
                                            {notice.text}
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <button
                                        onClick={() => submit('BACKEND_READY', '백엔드 연동을 요청했습니다. 연동 작업자 대기열에 올라갑니다.')}
                                        disabled={isSaving}
                                        className="flex items-center justify-center gap-2 bg-blue-600 text-white rounded-xl py-4 font-semibold text-[15px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                                    >
                                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                                        바로 연동 요청
                                    </button>

                                    <button
                                        onClick={() => submit('BIZ_PENDING', '비즈니스 인증 대기 상태로 저장했습니다.')}
                                        disabled={isSaving}
                                        className="flex items-center justify-center gap-2 bg-amber-500 text-white rounded-xl py-4 font-semibold text-[15px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-amber-600 transition-colors"
                                    >
                                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Building2 className="w-4 h-4"/>}
                                        인증 대기
                                    </button>

                                    <button
                                        onClick={() => submit(null, '입력한 내용을 저장했습니다.')}
                                        disabled={isSaving}
                                        /*
                                          🔴 색을 넣었다(2026-09-08). 흰 배경 + 회색 테두리라 "글씨만 떠 있는" 것처럼 보였다.
                                             다만 주 동작('바로 연동' 파랑)·대기('인증 대기' 주황)와는 구분돼야 하므로
                                             중립적인 진회색을 쓴다.
                                        */
                                        className="flex items-center justify-center gap-2 bg-slate-600 text-white rounded-xl py-4 font-semibold text-[15px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-slate-700 transition-colors"
                                    >
                                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                                        저장만 하기
                                    </button>
                                </div>

                                <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
                                    '바로 연동 요청' 은 카카오 채널 인증이 이미 끝난 경우에 누릅니다.
                                    인증을 이제 신청했다면 '인증 대기' 로 두고, 인증이 확인되면 다시 들어와 '바로 연동 요청' 을 누르세요.
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * 섹션 하나를 감싸는 박스.
 *
 * '기본 상태 정보' 와 같은 모양(둥근 테두리 + 옅은 회색 배경)으로 통일해, 화면이
 * **입력 묶음 단위**로 읽히게 한다. 종전에는 제목만 있고 경계가 없어 어디까지가 한 묶음인지
 * 눈으로 구분되지 않았다.
 *
 * @param accent 강조 박스(파란 테두리). '진행' 처럼 가장 먼저 봐야 하는 묶음에만 쓴다.
 */
function Panel({title, icon, badge, accent = false, children}) {
    return (
        <div className={`mb-8 rounded-2xl border p-5 ${
            accent ? 'border-2 border-blue-100 bg-blue-50/40' : 'border-gray-200 bg-gray-50/70'
        }`}>
            <div className="flex items-center gap-2 mb-4">
                {icon}
                <h3 className="text-[15px] font-bold text-gray-900">{title}</h3>
                {badge}
            </div>
            {children}
        </div>
    );
}

/**
 * 디자인 선택 한 칸.
 *
 * 빈 문자열(`''`)이 **'주문 안 함'** 이다 — 서버는 이 값을 NULL 로 저장하고,
 * NULL 이면 그 상품은 주문되지 않은 것으로 본다(`Dto.needsDeliveryInfo`).
 */
function Select({label, value, onChange, options, noneLabel, disabled = false}) {
    return (
        <div>
            <label className={`block text-[13px] font-semibold mb-1.5 ${disabled ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
            <select
                value={value}
                onChange={onChange}
                disabled={disabled}
                className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all ${
                    disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white'
                }`}
            >
                {/* `noneLabel` 이 null 이면 '없음' 선택지를 두지 않는다(방향처럼 항상 값이 있어야 하는 칸). */}
                {noneLabel != null && <option value="">{noneLabel}</option>}
                {options.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
            </select>
        </div>
    );
}

/**
 * 입력 한 칸.
 *
 * `maxLength` 를 화면에서도 막는 이유 = 서버가 컬럼 한도로 절단하므로, 여기서 막지 않으면
 * 담당자가 긴 값을 넣고 저장한 뒤 잘린 것을 나중에 발견한다.
 */
function Input({label, value, onChange, placeholder, maxLength, type = 'text', disabled = false}) {
    return (
        <div>
            <label className={`block text-[13px] font-semibold mb-1.5 ${disabled ? 'text-gray-300' : 'text-gray-700'}`}>
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                min={type === 'number' ? 0 : undefined}
                className={`w-full px-4 py-3 border rounded-xl text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-400 ${
                    disabled ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-200'
                }`}
            />
        </div>
    );
}
